import StayLoggedInModal from './StayLoggedInModal';
import { DynamicConf } from '@parship/psg-interfaces';

export default class LoginNavigation {
	private state: string;
	private loginForm: HTMLFormElement;
	private dynamicConf: DynamicConf;
	private stayLoggedInModal: StayLoggedInModal;
	private loginFormEmail: HTMLInputElement;

	public constructor() {
		this.loginForm = document.querySelector('#login');
		this.loginFormEmail = this.loginForm.querySelector('#loginFormEmail');
		this.dynamicConf = window.dynamicConf;
		this.stayLoggedInModal = new StayLoggedInModal(this.dynamicConf);

		if (this.dynamicConf?.isIovationActive) {
			this.initIovation();
		}

		document
			.querySelector('header.default a.login')
			.addEventListener('click', (event: MouseEvent) => this.toggleLoginNavigation(event));
		document
			.querySelector('.js-show-stay-logged-in-modal')
			.addEventListener('click', (event: Event) => {
				event.stopPropagation();
				event.preventDefault();
				this.stayLoggedInModal.show();
			});

		if (this.dynamicConf?.apple.isLoginAllowed) {
			const signInWithApple: HTMLAnchorElement = document.querySelector(
				'#appleLoginContainer .signInWithApple'
			);
			signInWithApple.href = this.getAppleButtonHref();
			document.querySelector('#appleLoginContainer').classList.add('is-visible');
		}

		if (this.dynamicConf?.google.isLoginAllowed) {
			const signInWithGoogle: HTMLAnchorElement = document.querySelector(
				'#googleLoginContainer .signInWithGoogle'
			);
			signInWithGoogle.href = this.getGoogleButtonHref();
			document.querySelector('#googleLoginContainer').classList.add('is-visible');
		}

		if (this.dynamicConf?.apple.isLoginAllowed || this.dynamicConf?.google.isLoginAllowed) {
			document.querySelector('.socialSignOnSeparator').classList.add('is-visible');
		}

		if (
			this.dynamicConf?.turnstile?.isEnabled &&
			this.loginForm.dataset.cfTurnstile === 'true' &&
			!window.navigator.userAgent.startsWith('PEG')
		) {
			this.initTurnstile();
		}

		window.addEventListener('resize', () => this.setFormHeight());
		this.setFormHeight();

		this.state = document.body.classList.contains('login-form-is-visible') ? 'open' : 'closed';

		if (this.state === 'open') {
			this.loginForm.removeAttribute('inert');
			this.loginFormEmail.focus();
		}
	}

	private getAppleButtonHref(): string {
		if (!this.dynamicConf.apple.isLoginAllowed) {
			return '';
		}

		const redirectUrl: string = encodeURIComponent(
			`${this.dynamicConf.apple.callbackDomain}/appleCallback`
		);

		let stateParams: string = `action=login&oid=${this.dynamicConf.ownerId}`;

		if (this.dynamicConf.apple.socialProviderState?.length) {
			stateParams += `&socialProviderState=${this.dynamicConf.apple.socialProviderState}`;
		}

		const state: string = encodeURIComponent(stateParams);
		const responseType: string = encodeURIComponent('code id_token');
		const responseMode: string = 'form_post';
		const scope: string = 'email';

		return `https://appleid.apple.com/auth/authorize?client_id=${this.dynamicConf.apple.clientId}&redirect_uri=${redirectUrl}&response_type=${responseType}&response_mode=${responseMode}&scope=${scope}&state=${state}`;
	}

	private getGoogleButtonHref(): string {
		const href: URL = new URL('https://accounts.google.com/o/oauth2/v2/auth');
		href.searchParams.append('client_id', this.dynamicConf.google.clientId);
		href.searchParams.append('response_type', 'code');
		href.searchParams.append('access_type', 'offline');
		href.searchParams.append('scope', 'profile email');
		href.searchParams.append('redirect_uri', `${this.dynamicConf.ownerUrl}/login`);

		if (this.dynamicConf.google.socialProviderState?.length) {
			href.searchParams.append(
				'state',
				encodeURIComponent(
					`socialProviderState=${this.dynamicConf.google.socialProviderState}`
				)
			);
		}
		return href.toString();
	}

	private initIovation(): void {
		const hiddenInputField: HTMLInputElement = document.createElement('input');
		hiddenInputField.type = 'hidden';
		hiddenInputField.name = 'fd';
		hiddenInputField.classList.add('fd');
		this.loginForm.prepend(hiddenInputField);

		const iovationScript: HTMLScriptElement = document.createElement('script');
		iovationScript.src = `/static_app/${this.dynamicConf.brandName}/_staticversion_/js/iov_combined.js`;

		document.body.appendChild(iovationScript);
	}

	private initTurnstile(): void {
		const id: string = `turnstile_${crypto.randomUUID()}`;
		const turnstileWrapper: HTMLDivElement = document.createElement('div');
		turnstileWrapper.id = id;
		turnstileWrapper.dataset.action = 'wp_login';
		this.loginForm.appendChild(turnstileWrapper);

		const renderTurnstile = () =>
			turnstile.render(`#${id}`, { sitekey: this.dynamicConf.turnstile.siteKey });

		if (typeof turnstile === 'undefined') {
			const script: HTMLScriptElement = document.createElement('script');
			script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
			script.defer = true;
			script.onload = renderTurnstile;
			document.body.appendChild(script);
		} else {
			renderTurnstile();
		}
	}

	public toggleLoginNavigation(event: MouseEvent): void {
		event.preventDefault();
		document.body.classList.add('transition');

		switch (this.state) {
			case 'closed':
				document.body.classList.add('login-form-is-visible');
				this.state = 'open';
				this.loginForm.removeAttribute('inert');
				this.loginFormEmail.focus();
				break;
			case 'open':
				document.body.classList.remove('login-form-is-visible');
				this.state = 'closed';
				this.loginForm.setAttribute('inert', '');
				break;
		}
	}

	private setFormHeight(): void {
		document.documentElement.style.setProperty(
			'--login-form-height',
			`${this.loginForm.offsetHeight}px`
		);
	}
}
